import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import {
  addCacheUpdateListener,
  isImageAlreadyLoaded,
  markImageAsLoaded,
} from '../../utility/thumbnails';
import SimpleLoadingSpinner from '../SimpleLoadingSpinner';

interface BRollFrameThumbnailsProps {
  videoUrl: string; // Video source URL to lookup muxPlaybackId
  trimStart: number; // Start time in seconds
  duration: number; // Duration in seconds
  elementWidth: number; // Width in pixels on timeline
  height: number; // Thumbnail height
  fallbackThumbnailUrl?: string; // URL for fallback thumbnail
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  overflow: hidden;
  position: relative;
  background: var(--surface);
`;

const FrameContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const PlaceholderFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const FrameImage = styled.img`
  height: 100%;
  object-fit: cover;
  transition: opacity var(--transition-normal) ease-in-out;
  will-change: opacity;
`;

// Frame calculation constants
const MIN_FRAME_WIDTH = 30; // Minimum frame width
const MAX_FRAME_WIDTH = 120; // Maximum frame width
const MAX_FRAMES = 50; // Limit frames for performance
const MAX_RETRY_ATTEMPTS = 3; // Maximum retry attempts for failed frames
const RETRY_DELAY = 2000; // Delay between retries in milliseconds

// Generate Mux thumbnail URL for a specific time
const getMuxThumbnailUrl = (
  muxPlaybackId: string,
  time: number,
  width: number,
  height: number,
): string => {
  return `https://image.mux.com/${muxPlaybackId}/thumbnail.jpg?time=${time.toFixed(
    1,
  )}&width=${width * 2}&height=${height * 2}&fit_mode=crop`;
};

// Check if frame should be retried
const shouldRetry = (frame: FrameData): boolean => {
  return (frame.retryCount || 0) < MAX_RETRY_ATTEMPTS && !!frame.thumbnailUrl;
};

interface FrameData {
  index: number;
  time: number;
  width: number;
  thumbnailUrl: string; // Mux thumbnail URL (empty string if no muxPlaybackId)
  retryCount?: number; // Track retry attempts for failed frames
  isRetrying?: boolean; // Track if frame is currently retrying
  isLoading?: boolean; // Track if image is currently loading
  isLoaded?: boolean; // Track if image has successfully loaded
}

export const BRollFrameThumbnails: React.FC<BRollFrameThumbnailsProps> =
  observer(({ videoUrl, trimStart, duration, elementWidth, height }) => {
    const [frames, setFrames] = useState<FrameData[]>([]);
    const videoCreator = useVideoCreatorStore();

    // Track which images we've already processed to prevent infinite loops
    const processedImagesRef = useRef<Set<string>>(new Set());

    // Retry logic for failed frames
    const retryFrame = useCallback((frameIndex: number) => {
      setFrames(prevFrames =>
        prevFrames.map(frame =>
          frame.index === frameIndex
            ? {
                ...frame,
                retryCount: (frame.retryCount || 0) + 1,
                isRetrying: true,
                isLoading: true,
                isLoaded: false,
              }
            : frame,
        ),
      );

      // Add delay before retry
      setTimeout(() => {
        setFrames(prevFrames =>
          prevFrames.map(frame =>
            frame.index === frameIndex
              ? { ...frame, isRetrying: false }
              : frame,
          ),
        );
      }, RETRY_DELAY);
    }, []);

    // Mark frame as successfully loaded
    const markFrameLoaded = useCallback(
      (frameIndex: number, imageSrc: string) => {
        // Add to global cache and notify other components
        markImageAsLoaded(imageSrc);

        setFrames(prevFrames =>
          prevFrames.map(frame =>
            frame.index === frameIndex
              ? { ...frame, isLoading: false, isLoaded: true }
              : frame,
          ),
        );
      },
      [],
    );

    // Handle image ref to check for cached images that don't fire onLoad
    const handleImageRef = useCallback(
      (img: HTMLImageElement | null, frameIndex: number, imageSrc: string) => {
        if (img && img.complete && img.naturalWidth > 0) {
          // Create a unique key for this frame to prevent duplicate processing
          const frameKey = `${frameIndex}-${imageSrc}`;
          if (!processedImagesRef.current.has(frameKey)) {
            processedImagesRef.current.add(frameKey);
            // Image is already loaded (cached), manually trigger loaded state
            markFrameLoaded(frameIndex, imageSrc);
          }
        }
      },
      [markFrameLoaded],
    );

    // Check if video is being watched for muxPlaybackId updates
    const isWaitingForMuxProcessing = useMemo(() => {
      const storyId = videoCreator?.story?.id;
      const organizationIds =
        videoCreator?.story?._allReferencingShowcases?.map(s => s.id) || [];

      // Check if video is in story video artifacts watch list
      if (storyId) {
        const watchedStoryVideos =
          videoCreator.statusUpdateManager.watchedStoryVideoArtifacts[
            storyId
          ] || [];
        if (
          watchedStoryVideos.some(
            (v: { id: string; url: string }) => v.url === videoUrl,
          )
        ) {
          return true;
        }
      }

      // Check if video is in organization video artifacts watch list
      for (const orgId of organizationIds) {
        const watchedOrgVideos =
          videoCreator.statusUpdateManager.watchedOrganizationVideoArtifacts[
            orgId
          ] || [];
        if (
          watchedOrgVideos.some(
            (v: { id: string; url: string }) => v.url === videoUrl,
          )
        ) {
          return true;
        }
      }

      return false;
    }, [
      videoUrl,
      videoCreator?.story?.id,
      videoCreator?.story?._allReferencingShowcases,
      videoCreator?.statusUpdateManager.watchedStoryVideoArtifacts,
      videoCreator?.statusUpdateManager.watchedOrganizationVideoArtifacts,
    ]);

    // Lookup muxPlaybackId from story or organization video artifacts
    const muxPlaybackId = useMemo(() => {
      const storyVideo = videoCreator?.story?.storyArtifactsVideo?.find(
        artifact => artifact.url === videoUrl,
      );
      if (storyVideo?.video?.muxPlaybackId) {
        return storyVideo.video.muxPlaybackId;
      }

      const organizationVideo = (
        videoCreator.story?._allReferencingShowcases || []
      )
        .flatMap(album => album.organizationArtifactsVideo || [])
        .find(artifact => artifact.url === videoUrl);

      if (organizationVideo?.video?.muxPlaybackId) {
        return organizationVideo.video.muxPlaybackId;
      }

      return null;
    }, [
      videoUrl,
      videoCreator?.story?.storyArtifactsVideo,
      videoCreator.story?._allReferencingShowcases,
    ]);

    // Calculate frames to display
    const frameData = useMemo(() => {
      // Calculate optimal frame width
      const aspectRatio = 4 / 3;
      const optimalFrameWidth = Math.round(height * aspectRatio);

      // Clamp frame width to reasonable bounds
      const frameWidth = Math.max(
        MIN_FRAME_WIDTH,
        Math.min(MAX_FRAME_WIDTH, optimalFrameWidth),
      );

      // Calculate frame count based on available space
      let frameCount = Math.max(1, Math.floor(elementWidth / frameWidth));

      // Limit frame count to available duration and performance limits
      const maxAvailableFrames = Math.floor(duration * 2); // Up to 2 frames per second
      frameCount = Math.min(frameCount, maxAvailableFrames, MAX_FRAMES);

      // Generate array of frame times
      const frameInterval = duration / frameCount;

      return Array.from({ length: frameCount }, (_, i) => {
        const frameTime = trimStart + i * frameInterval + frameInterval / 2;
        return {
          index: i,
          time: frameTime,
          width: elementWidth / frameCount,
          thumbnailUrl: muxPlaybackId
            ? getMuxThumbnailUrl(muxPlaybackId, frameTime, frameWidth, height)
            : '',
          retryCount: 0,
          isRetrying: false,
          isLoading: true,
          isLoaded: false,
        };
      });
    }, [trimStart, duration, elementWidth, height, muxPlaybackId]);

    // Update frames when frameData changes
    useEffect(() => {
      if (frameData.length === 0) return;

      // Clear processed images when frameData changes
      processedImagesRef.current.clear();

      // Always update frames to match frameData, preserving existing state
      setFrames(prevFrames =>
        frameData.map(newFrame => {
          const prevFrame = prevFrames.find(f => f.index === newFrame.index);
          const thumbnailUrlChanged =
            prevFrame?.thumbnailUrl !== newFrame.thumbnailUrl;
          const shouldResetLoading =
            thumbnailUrlChanged || !prevFrame?.isLoaded;

          return {
            ...newFrame,
            retryCount: 0,
            isRetrying: false,
            isLoading: shouldResetLoading,
            isLoaded: prevFrame?.isLoaded || false,
          };
        }),
      );
    }, [frameData]);

    // Check for cached images when frameData changes
    useEffect(() => {
      setFrames(prevFrames =>
        prevFrames.map(frame => {
          if (
            frame.thumbnailUrl &&
            !frame.isLoaded &&
            isImageAlreadyLoaded(frame.thumbnailUrl)
          ) {
            return { ...frame, isLoaded: true, isLoading: false };
          }
          return frame;
        }),
      );
    }, [frameData]);

    // Listen for global cache updates from other components
    useEffect(() => {
      const checkCachedImages = () => {
        setFrames(prevFrames => {
          let hasUpdates = false;
          const updatedFrames = prevFrames.map(frame => {
            if (
              frame.thumbnailUrl &&
              !frame.isLoaded &&
              isImageAlreadyLoaded(frame.thumbnailUrl)
            ) {
              hasUpdates = true;
              return { ...frame, isLoaded: true, isLoading: false };
            }
            return frame;
          });
          return hasUpdates ? updatedFrames : prevFrames;
        });
      };

      return addCacheUpdateListener(checkCachedImages);
    }, []);

    // Show loading spinner when no mux data and either waiting for processing or no frames
    if (!muxPlaybackId && (isWaitingForMuxProcessing || frames.length === 0)) {
      return (
        <Container style={{ width: '100%', height }}>
          <SimpleLoadingSpinner
            text={null}
            icon={null}
            spinnerSize={16}
            spinnerWidth={3}
          />
        </Container>
      );
    }

    return (
      <Container style={{ height }}>
        {frameData.map(frame => {
          const frameWithData = frames.find(f => f.index === frame.index);

          const imageSrc = frame.thumbnailUrl;

          if (imageSrc && imageSrc !== '') {
            const isRetrying = frameWithData?.isRetrying || false;
            const hasExceededRetries =
              (frameWithData?.retryCount || 0) >= MAX_RETRY_ATTEMPTS;
            // If frameWithData doesn't exist yet, assume not loaded
            const isLoading = frameWithData?.isLoading;
            // Check both component state and global cache for loaded status
            const isLoadedFromState = frameWithData
              ? frameWithData.isLoaded
              : false;
            const isLoadedFromCache = isImageAlreadyLoaded(imageSrc);
            const isLoaded = isLoadedFromState || isLoadedFromCache;

            return (
              <FrameContainer
                key={frame.index}
                style={{ width: frame.width, height }}
              >
                {!isRetrying && !hasExceededRetries && (
                  <FrameImage
                    ref={(img: HTMLImageElement | null) =>
                      handleImageRef(img, frame.index, imageSrc)
                    }
                    src={imageSrc}
                    style={{
                      width: frame.width,
                      opacity: isLoading ? 0.3 : isLoaded ? 1 : 0,
                    }}
                    alt={`Frame at ${frame.time.toFixed(1)}s`}
                    onLoad={() => markFrameLoaded(frame.index, imageSrc)}
                    onError={() => {
                      const currentFrame = frameWithData || frame;
                      if (shouldRetry(currentFrame)) retryFrame(frame.index);
                    }}
                  />
                )}
                {(isRetrying ||
                  hasExceededRetries ||
                  !isLoaded ||
                  isLoading) && (
                  <PlaceholderFrame>
                    {(isRetrying || !isLoaded || isLoading) && (
                      <SimpleLoadingSpinner
                        text={null}
                        icon={null}
                        spinnerSize={16}
                        spinnerWidth={3}
                      />
                    )}
                  </PlaceholderFrame>
                )}
              </FrameContainer>
            );
          }

          // Show fallback thumbnail as last resort
          if (
            !isWaitingForMuxProcessing &&
            !frameWithData?.isRetrying &&
            (frameWithData?.retryCount || 0) >= MAX_RETRY_ATTEMPTS
          ) {
            return (
              <div key={frame.index} style={{ width: frame.width, height }} />
            );
          }

          // Show placeholder if no fallback available
          return (
            <PlaceholderFrame
              key={frame.index}
              style={{ width: frame.width, height }}
            >
              {isWaitingForMuxProcessing ? (
                <SimpleLoadingSpinner
                  text={null}
                  icon={null}
                  spinnerSize={16}
                  spinnerWidth={3}
                />
              ) : null}
            </PlaceholderFrame>
          );
        })}
      </Container>
    );
  });
